import { ref } from 'vue';
import { useLocale } from './useLocale';

import { get } from '@/api/httpClient';
import { DEFAULT_MIN_PRICE, DEFAULT_MAX_PRICE } from '@/constants/rentalConstants';
import { DEFAULT_PURCHASE_MIN_PRICE, DEFAULT_PURCHASE_MAX_PRICE } from '@/constants/purchaseConstants';
import type { GetUnitsRequest } from '@/typings/requests/GetUnitsRequest';
import type { Unit } from '@/typings/units/Unit';
import type { RentalUnitFilters } from '@/typings/units/RentalUnitFilters';
import { UnitStatusEnum } from '@/enums/UnitStatus';
import { ListingTypeEnum } from '@/enums/ListingType';

export interface PaginatedUnitsResult {
  units: Unit[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export function useUnitsApi() {
  const { getLocale } = useLocale();

  // GET refs
  const getUnitsByBuildingIdError = ref<string | undefined>();
  const getUnitsByBuildingIdLoading = ref(false);

  const getUnitsByProjectIdError = ref<string | undefined>();
  const getUnitsByProjectIdLoading = ref(false);

  const getUnitFiltersError = ref<string | undefined>();
  const getUnitFiltersLoading = ref(false);

  const listUnitsError = ref<string | undefined>();
  const listUnitsLoading = ref(false);

  // GET method
  async function getUnitsByBuildingId(id: number, request?: GetUnitsRequest): Promise<Unit[]> {
    getUnitsByBuildingIdLoading.value = true;
    try {
      // Utilise l'endpoint général /units qui supporte tous les filtres
      const params = new URLSearchParams();
      const lang = getLocale();
      
      // Ajouter l'ID du bâtiment
      params.append('buildings', id.toString());
      
      // Ajouter tous les paramètres de filtrage
      if (request?.statuses) {
        request.statuses.forEach(status => params.append('statuses', status.toString()));
      }
      
      if (request?.listingTypes) {
        request.listingTypes.forEach(type => params.append('listingTypes', type.toString()));
      }
      
      if (request?.floorNumbers && request.floorNumbers.length > 0) {
        request.floorNumbers.forEach(floor => params.append('floorNumbers', floor.toString()));
      }
      
      if (request?.unitConfigurations && request.unitConfigurations.length > 0) {
        request.unitConfigurations.forEach(config => params.append('unitConfigurations', config));
      }
      
      if (request?.availabilityDates && request.availabilityDates.length > 0) {
        request.availabilityDates.forEach(date => {
          params.append('availabilityDates', date);
        });
      }
      
      if (request?.dogAllowed !== undefined && request?.dogAllowed !== null) {
        params.append('dogAllowed', request.dogAllowed.toString());
      }
      
      if (request?.minPrice !== undefined && request?.minPrice !== null) {
        params.append('minPrice', request.minPrice.toString());
      }
      
      if (request?.maxPrice !== undefined && request?.maxPrice !== null) {
        params.append('maxPrice', request.maxPrice.toString());
      }
      
      // Always include locale from i18n (fr or en)
      params.append('locale', lang);
      
      const url = `/units${params.toString() ? `?${params.toString()}` : ''}`;
      return await get<undefined, Unit[]>(url);
    } catch (err) {
      getUnitsByBuildingIdError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load units:', getUnitsByBuildingIdError.value);
      return [];
    } finally {
      getUnitsByBuildingIdLoading.value = false;
    }
  }

  async function getUnitsByProjectId(id: number, request?: GetUnitsRequest): Promise<Unit[]> {
    getUnitsByProjectIdLoading.value = true;
    try {
      const lang = getLocale();
      return await get<any, Unit[]>('/units', {
        ...(request as any),
        projects: [id],
        locale: lang
      });
    } catch (err) {
      getUnitsByProjectIdError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load units:', getUnitsByProjectIdError.value);
      return [];
    } finally {
      getUnitsByProjectIdLoading.value = false;
    }
  }

  async function getUnitFilters(listingType: ListingTypeEnum, unitStatuses?: UnitStatusEnum[] ): Promise<RentalUnitFilters> {
    getUnitFiltersLoading.value = true;
    try {
      const params = new URLSearchParams();
      const lang = getLocale();
      
      if (unitStatuses) {
        unitStatuses.forEach(status => params.append('unitStatuses', status.toString()));
      }
      params.append('listingType', listingType.toString());
      params.append('locale', lang);
      
      const url = `/units/rental-filters${params.toString() ? `?${params.toString()}` : ''}`;
      return await get<undefined, RentalUnitFilters>(url);
    } catch (err) {
      getUnitFiltersError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load rental unit filters:', getUnitFiltersError.value);
      return { 
        availableFloors: [], 
        minPrice: listingType === ListingTypeEnum.Rental ? DEFAULT_MIN_PRICE : DEFAULT_PURCHASE_MIN_PRICE, 
        maxPrice: listingType === ListingTypeEnum.Rental ? DEFAULT_MAX_PRICE : DEFAULT_PURCHASE_MAX_PRICE 
      };
    } finally {
      getUnitFiltersLoading.value = false;
    }
  }

  async function listUnits(request?: GetUnitsRequest): Promise<Unit[]> {
    listUnitsLoading.value = true;
    try {
      const params = new URLSearchParams();
      const lang = getLocale();
      
      // Ajouter tous les paramètres de filtrage
      if (request?.regionIds && request.regionIds.length > 0) {
        request.regionIds.forEach(regionId => params.append('regionIds', regionId.toString()));
      }

      if (request?.statuses) {
        request.statuses.forEach(status => params.append('statuses', status.valueOf().toString()));
      }
      
      if (request?.listingTypes) {
        request.listingTypes.forEach(type => params.append('listingTypes', type.valueOf().toString()));
      }
      
      if (request?.projects && request.projects.length > 0) {
        request.projects.forEach(projectId => params.append('projects', projectId.toString()));
      }
      
      if (request?.buildings && request.buildings.length > 0) {
        request.buildings.forEach(buildingId => params.append('buildings', buildingId.toString()));
      }
      
      if (request?.floorNumbers && request.floorNumbers.length > 0) {
        request.floorNumbers.forEach(floor => params.append('floorNumbers', floor.toString()));
      }
      
      if (request?.unitConfigurations && request.unitConfigurations.length > 0) {
        request.unitConfigurations.forEach(config => params.append('unitConfigurations', config));
      }
      
      if (request?.availabilityDates) {
        request.availabilityDates.forEach(date => params.append('availabilityDates', date.toString()));
      }
      
      if (request?.dogAllowed != null) {
        params.append('dogAllowed', request.dogAllowed.toString());
      }
      
      if (request?.minPrice != null) {
        params.append('minPrice', request.minPrice.toString());
      }
      
      if (request?.maxPrice != null) {
        params.append('maxPrice', request.maxPrice.toString());
      }
      
      // Always include locale from i18n (fr or en)
      params.append('locale', lang);
      
      if (request?.page != null) {
        params.append('page', request.page.toString());
      }
      
      if (request?.pageSize != null) {
        params.append('pageSize', request.pageSize.toString());
      }
      
      const url = `/units${params.toString() ? `?${params.toString()}` : ''}`;
      return await get<undefined, Unit[]>(url);
    } catch (err) {
      listUnitsError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load units:', listUnitsError.value);
      return [];
    } finally {
      listUnitsLoading.value = false;
    }
  }

  async function listUnitsPaginated(request: GetUnitsRequest & { page: number; pageSize: number }): Promise<PaginatedUnitsResult> {
    listUnitsLoading.value = true;
    try {
      const params = new URLSearchParams();
      const lang = getLocale();
      
      // Ajouter tous les paramètres de filtrage
      if (request?.regionIds && request.regionIds.length > 0) {
        request.regionIds.forEach(regionId => params.append('regionIds', regionId.toString()));
      }

      if (request?.statuses) {
        request.statuses.forEach(status => params.append('statuses', status.valueOf().toString()));
      }
      
      if (request?.listingTypes) {
        request.listingTypes.forEach(type => params.append('listingTypes', type.valueOf().toString()));
      }
      
      if (request?.projects && request.projects.length > 0) {
        request.projects.forEach(projectId => params.append('projects', projectId.toString()));
      }
      
      if (request?.buildings && request.buildings.length > 0) {
        request.buildings.forEach(buildingId => params.append('buildings', buildingId.toString()));
      }
      
      if (request?.floorNumbers && request.floorNumbers.length > 0) {
        request.floorNumbers.forEach(floor => params.append('floorNumbers', floor.toString()));
      }
      
      if (request?.unitConfigurations && request.unitConfigurations.length > 0) {
        request.unitConfigurations.forEach(config => params.append('unitConfigurations', config));
      }
      
      if (request?.availabilityDates) {
        request.availabilityDates.forEach(date => params.append('availabilityDates', date.toString()));
      }
      
      if (request?.dogAllowed != null) {
        params.append('dogAllowed', request.dogAllowed.toString());
      }
      
      if (request?.minPrice != null) {
        params.append('minPrice', request.minPrice.toString());
      }
      
      if (request?.maxPrice != null) {
        params.append('maxPrice', request.maxPrice.toString());
      }
      
      // Always include locale from i18n (fr or en)
      params.append('locale', lang);
      
      params.append('page', request.page.toString());
      params.append('pageSize', request.pageSize.toString());
      
      // Utiliser la même logique que httpClient
      const baseDomain = window.location.origin;
      const baseUrl = `${baseDomain}/umbraco/api`;
      const url = `${baseUrl}/units?${params.toString()}`;
      
      // Utiliser fetch pour accéder aux headers
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const units: Unit[] = await response.json();
      
      // Lire les headers de pagination
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const currentPage = parseInt(response.headers.get('X-Current-Page') || '1');
      const pageSize = parseInt(response.headers.get('X-Page-Size') || request.pageSize.toString());
      const totalPages = parseInt(response.headers.get('X-Total-Pages') || '1');
      
      return {
        units,
        totalCount,
        currentPage,
        pageSize,
        totalPages
      };
    } catch (err) {
      listUnitsError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load units:', listUnitsError.value);
      return {
        units: [],
        totalCount: 0,
        currentPage: 1,
        pageSize: request.pageSize,
        totalPages: 0
      };
    } finally {
      listUnitsLoading.value = false;
    }
  }

  return {
    // GET returns
    getUnitsByBuildingId,
    getUnitsByBuildingIdError,
    getUnitsByBuildingIdLoading,
    getUnitsByProjectId,
    getUnitsByProjectIdError,
    getUnitsByProjectIdLoading,
    getUnitFilters,
    getUnitFiltersError,
    getUnitFiltersLoading,
    listUnits,
    listUnitsPaginated,
    listUnitsError,
    listUnitsLoading
  };
}
